import { ClassRating } from "constants/rate-classes";
import { generateUUID } from "utils/utils";
import wretch from "wretch";
import {
  ActorType,
  Entity,
  IPendingFeedbackAPIResponse,
  IRestructuredPendingFeedbacksAPIResponse,
  ISubmitClassFeedbackPayload,
  ISubmitClassFeedbackResponse,
  IUpdateClassFeedbackPayload,
  IUpdateClassFeedbackResponse,
  Tag
} from "./classFeedbackService.types";

const BASE_URL = process.env.NEXT_PUBLIC_BASE_API;

export const getPendingClassFeedbacks = async (
  userToken: string
): Promise<IRestructuredPendingFeedbacksAPIResponse> => {
  const URL = `${BASE_URL}/feedbacks/v1/pending`;
  const response: IPendingFeedbackAPIResponse = await wretch(URL)
    .headers({
      "x-iq-request-id": generateUUID(),
      "X-IQ-Locale": "id"
    })
    .auth(`Bearer ${userToken}`)
    .get()
    .json();
  return restructurePendingClassFeedbacks(response);
};

export const submitClassFeedback = (
  userToken: string,
  payload: ISubmitClassFeedbackPayload
): Promise<ISubmitClassFeedbackResponse> => {
  const URL = `${BASE_URL}/feedbacks/v1/pending`;
  return wretch(URL)
    .headers({
      "x-iq-request-id": generateUUID(),
      "X-IQ-Locale": "id"
    })
    .auth(`Bearer ${userToken}`)
    .post(payload)
    .json();
};

export const updateClassFeedback = (
  userToken: string,
  { user_feedback_id, ...restOfPayload }: IUpdateClassFeedbackPayload
): Promise<IUpdateClassFeedbackResponse> => {
  const URL = `${BASE_URL}/feedbacks/v1/pending/${user_feedback_id}`;
  return wretch(URL)
    .headers({
      "x-iq-request-id": generateUUID(),
      "X-IQ-Locale": "id"
    })
    .auth(`Bearer ${userToken}`)
    .put(restOfPayload)
    .json();
};

function restructurePendingClassFeedbacks({
  data
}: IPendingFeedbackAPIResponse): IRestructuredPendingFeedbacksAPIResponse {
  if (!data) {
    return {
      feedbacks: [],
      metaData: {
        ratings: [],
        tagsByRating: {}
      }
    };
  }
  const { feedbacks, partial_feedbacks, media_base_url, meta } = data;

  const subjectActors = meta.actors.filter(
    (actor) => actor.type === ActorType.SUBJECT
  );
  const teacherActors = meta.actors.filter(
    (actor) => actor.type === ActorType.TEACHER
  );

  const pendingFeedbacks = feedbacks.map((pendingFeedback) => {
    const subjects = subjectActors
      .filter((subjectActor) =>
        pendingFeedback.actor_list.includes(subjectActor.id)
      )
      .map(({ id, name }) => {
        const logoObj = meta.assets.find((asset) => asset.id === id);
        return {
          id,
          name,
          logo: logoObj ? `${media_base_url}/${logoObj.png}` : null
        };
      });

    const teachers = teacherActors
      .filter((teacherActor) =>
        pendingFeedback.actor_list.includes(teacherActor.id)
      )
      .map(({ id, name }) => ({ id, name }));

    const liveClass = meta.entities.find(
      (entity) => entity.id === pendingFeedback.entity_id
    ) as Entity;

    const previousState = partial_feedbacks.find(
      (partialFeedback) =>
        partialFeedback.entity_id === pendingFeedback.entity_id
    );

    return {
      id: pendingFeedback.id,
      subject: subjects[0],
      teachers,
      liveClass,
      previousState: previousState
        ? {
            rating: previousState.rating,
            userFeedbackId: previousState.user_feedback_id,
            liveClassId: previousState.entity_id,
            status: previousState.status
          }
        : null
    };
  });

  const ratings = meta.tags.reduce(
    (
      acc: { rating: ClassRating; rating_id: string }[],
      { rating, rating_id }
    ) => {
      acc.push({
        rating,
        rating_id
      });
      return acc;
    },
    []
  );

  const tagsByRating = meta.tags.reduce((acc: Record<string, Tag[]>, tag) => {
    if (acc[tag.rating]) {
      acc[tag.rating].push(tag);
    } else {
      acc[tag.rating] = [tag];
    }
    return acc;
  }, {});

  return {
    feedbacks: pendingFeedbacks,
    metaData: {
      ratings,
      tagsByRating
    }
  };
}
